import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Get the base URL for title generation API
const API_BASE_URL_TITLE = `${API_BASE_URL}/title-generation`;

//console.log('Title Generation API base URL:', API_BASE_URL_TITLE);

/**
 * Get authentication headers with proper content type for JSON
 * @returns Headers object with authorization and content-type
 */
const getJsonAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  console.log('JSON Auth headers:', headers); // Debug log
  
  return headers;
};

/**
 * Get authentication headers for FormData (without Content-Type)
 * @returns Headers object with authorization only
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  console.log('Auth headers:', headers); // Debug log
  
  return headers;
};

/**
 * Generate titles and descriptions from a video file
 * @param videoFile The video file to analyze
 * @param description Optional description to enhance generation
 * @param projectId Optional existing project ID to update
 * @returns Promise containing the project ID
 */
export const generateTitlesFromVideo = async (
  videoFile: File,
  description?: string,
  projectId?: string
): Promise<string> => {
  try {
    console.log('Generating titles from video', { fileName: videoFile.name, fileSize: videoFile.size, description });
    
    const formData = new FormData();
    formData.append('mediaFile', videoFile);
    
    if (description) {
      formData.append('description', description);
    }
    
    if (projectId) {
      formData.append('projectId', projectId);
      console.log('Using existing project ID:', projectId);
    }
    
    console.log('Sending request to API');
    
    // For debugging
    for (const [key, value] of formData.entries()) {
      console.log(`FormData ${key}: ${value instanceof File ? value.name : value}`);
    }
    
    const authHeaders = getAuthHeaders();
    console.log('Sending FormData request with auth headers:', authHeaders);
    
    const response = await axios.post(`${API_BASE_URL_TITLE}/generate`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...authHeaders,
      },
    });
    
    console.log('Received response from API', response.data);
    
    return response.data.projectId;
  } catch (error) {
    console.error('Error generating titles from video:', error);
    
    // For debugging
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
    }
    
    // If there's an error, try a direct text-based approach as fallback
    if (description) {
      console.log('Falling back to text-based title generation');
      return generateTitlesFromText(description);
    }
    
    throw error;
  }
};

/**
 * Generate titles and descriptions from an audio file
 * @param audioFile The audio file to analyze
 * @param description Optional description to enhance generation
 * @param projectId Optional existing project ID to update
 * @returns Promise containing the project ID
 */
export const generateTitlesFromAudio = async (
  audioFile: File,
  description?: string,
  projectId?: string
): Promise<string> => {
  try {
    console.log('Generating titles from audio', { fileName: audioFile.name, fileSize: audioFile.size, description });
    
    const formData = new FormData();
    formData.append('mediaFile', audioFile);
    
    if (description) {
      formData.append('description', description);
    }
    
    if (projectId) {
      formData.append('projectId', projectId);
      console.log('Using existing project ID:', projectId);
    }
    
    const authHeaders = getAuthHeaders();
    console.log('Sending FormData request with auth headers:', authHeaders);
    
    const response = await axios.post(`${API_BASE_URL_TITLE}/generate`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...authHeaders,
      },
    });
    
    return response.data.projectId;
  } catch (error) {
    console.error('Error generating titles from audio:', error);
    throw error;
  }
};

/**
 * Generate titles and descriptions from an audio or video URL (such as an S3 URL)
 * @param mediaUrl The URL of the audio or video file
 * @param mediaType The type of media ('audio' or 'video')
 * @param description Optional description to enhance generation
 * @param projectId Optional existing project ID to update
 * @returns Promise containing the project ID
 */
export const generateTitlesFromMediaUrl = async (
  mediaUrl: string,
  mediaType: 'audio' | 'video' | 'none',
  description?: string,
  projectId?: string
): Promise<string> => {
  try {
    console.log(`Generating titles from ${mediaType} URL`, { mediaUrl, description });
    
    // Send the URL directly in the JSON payload
    const payload: any = {
      description,
    };
    
    // Set the appropriate URL field based on media type
    if (mediaType === 'audio') {
      payload.audioUrl = mediaUrl;
    } else if (mediaType === 'video') {
      payload.videoUrl = mediaUrl;
    } else {
      // For 'none' media type, we'll rely on the description only
      console.log('No media type specified, using text-based generation');
      // If we have a mediaUrl but no specific type, default to video
      if (mediaUrl) {
        payload.videoUrl = mediaUrl;
      }
    }
    
    // Include project ID if provided
    if (projectId) {
      payload.projectId = projectId;
      console.log('Using existing project ID:', projectId);
    }
    
    const authHeaders = getJsonAuthHeaders();
    console.log('Sending JSON request with auth headers:', authHeaders);
      
    const response = await axios.post(`${API_BASE_URL_TITLE}/generate`, payload, {
      headers: authHeaders,
    });
    
    console.log('Received response from API', response.data);
    
    return response.data.projectId;
  } catch (error) {
    console.error(`Error generating titles from ${mediaType} URL:`, error);
    
    // For debugging
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
    }
    
    // If there's an error and we have a description, try a direct text-based approach as fallback
    if (description) {
      console.log('Falling back to text-based title generation');
      return generateTitlesFromText(description);
    }
    
    throw error;
  }
};

/**
 * Generate titles and descriptions from text description only
 * @param description The text description to use for generation
 * @param projectId Optional existing project ID to update
 * @returns Promise containing the project ID
 */
export const generateTitlesFromText = async (
  description: string,
  projectId?: string
): Promise<string> => {
  try {
    console.log('Generating titles from text description', { description });
    
    const payload: any = {
      description,
    };
    
    // Include project ID if provided
    if (projectId) {
      payload.projectId = projectId;
      console.log('Using existing project ID:', projectId);
    }
    
    const authHeaders = getJsonAuthHeaders();
    console.log('Sending JSON request with auth headers:', authHeaders);
      
    const response = await axios.post(`${API_BASE_URL_TITLE}/generate`, payload, {
      headers: authHeaders,
    });
    
    console.log('Received response from API', response.data);
    
    return response.data.projectId;
  } catch (error) {
    console.error('Error generating titles from text:', error);
    
    // For debugging
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
    }
    
    throw error;
  }
};

/**
 * Get title data for a project
 * @param projectId The project ID
 * @returns Promise containing the title data
 */
export const getTitleData = async (projectId: string): Promise<any> => {
  try {
    console.log('Making request to:', `${API_BASE_URL_TITLE}/project/${projectId}`);
    const headers = getJsonAuthHeaders();
    console.log('With headers:', headers);
    
    const response = await axios.get(`${API_BASE_URL_TITLE}/project/${projectId}`, {
      headers,
    });
    
    console.log('API Response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting title data:', error);
    if (axios.isAxiosError(error) && error.response) {
      console.error('API error response:', error.response.data);
      console.error('API error status:', error.response.status);
    }
    throw error;
  }
};

/**
 * Save title selection
 * @param projectId The project ID
 * @param titleIndex The index of the selected title
 * @returns Promise containing the updated title data
 */
export const saveTitleSelection = async (
  projectId: string,
  titleIndex: number
): Promise<any> => {
  try {
    const response = await axios.put(`${API_BASE_URL}/title-generation/project/${projectId}/title`, {
      titleIndex,
    }, {
      headers: getJsonAuthHeaders(),
    });
    
    return response.data;
  } catch (error) {
    console.error('Error saving title selection:', error);
    throw error;
  }
};

/**
 * Update a title
 * @param projectId The project ID
 * @param titleIndex The index of the title to update
 * @param newTitle The new title text
 * @returns Promise containing the updated title data
 */
export const updateTitle = async (
  projectId: string,
  titleIndex: number,
  newTitle: string
): Promise<any> => {
  try {
    const response = await axios.put(`${API_BASE_URL}/title-generation/project/${projectId}/title`, {
      titleIndex,
      newTitle,
    }, {
      headers: getJsonAuthHeaders(),
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating title:', error);
    throw error;
  }
};

/**
 * Update the description
 * @param projectId The project ID
 * @param description The new description
 * @returns Promise containing the updated description data
 */
export const updateDescription = async (
  projectId: string,
  description: string
): Promise<any> => {
  try {
    const response = await axios.put(`${API_BASE_URL}/title-generation/project/${projectId}/description`, {
      description,
    }, {
      headers: getJsonAuthHeaders(),
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating description:', error);
    throw error;
  }
};

/**
 * Update hashtags
 * @param projectId The project ID
 * @param hashtags Array of hashtags
 * @returns Promise containing the updated hashtags data
 */
export const updateHashtags = async (
  projectId: string,
  hashtags: string[]
): Promise<any> => {
  try {
    const response = await axios.put(`${API_BASE_URL}/title-generation/project/${projectId}/hashtags`, {
      hashtags,
    }, {
      headers: getJsonAuthHeaders(),
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating hashtags:', error);
    throw error;
  }
};

/**
 * Update timeline
 * @param projectId The project ID
 * @param timeline Array of timeline items
 * @returns Promise containing the updated timeline data
 */
export const updateTimeline = async (
  projectId: string,
  timeline: Array<{ time: string; description: string }>
): Promise<any> => {
  try {
    const response = await axios.put(`${API_BASE_URL}/title-generation/project/${projectId}/timeline`, {
      timeline,
    }, {
      headers: getJsonAuthHeaders(),
    });
    
    return response.data;
  } catch (error) {
    console.error('Error updating timeline:', error);
    throw error;
  }
};